import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ShellAtnaTurn, ShellTurn } from '../types/atna'
import { useLiveTruck } from './useLiveTruck'

export type CollecteKind =
  | 'camion-en-route'
  | 'camion-passe'
  | 'bac-ok'
  | 'requete-ouverte'
  | 'reponse'
  | 'suivi'
  /** Question sur les piles / batteries : non, pas au recyclage. */
  | 'batterie'
  /** Carte des points de dépôt les plus proches. */
  | 'batterie-lieux'

const THINKING_MS = 800

/** Reconnaît une question sur les piles ou batteries. */
export function isBatteryQuestion(text: string): boolean {
  return /batter|\bpiles?\b/i.test(text)
}

/** Reconnaît une phrase qui décrit un bac laissé sur le bord de la rue. */
export function isMissedBinReport(text: string): boolean {
  const lower = text.toLowerCase()
  return /bac|poubelle|bin/.test(lower) && /non ramass|pas ramass|oubli|pas vid|encore plein|toujours plein/.test(lower)
}

function isYes(text: string): boolean {
  return /^(oui|yes|ok|c’est bon|c'est bon|parfait)\b/i.test(text.trim())
}

function isNo(text: string): boolean {
  return /^(non|no|pas)\b/i.test(text.trim())
}

function timeNow(): string {
  const now = new Date()
  return `${now.getHours()} h ${String(now.getMinutes()).padStart(2, '0')}`
}

/**
 * Flux ATNA du module Collectes :
 * 1. « bac non ramassé » pendant la collecte → ATNA rassure, camion en direct ;
 * 2. dès que le camion arrive (même si la conversation reste ouverte), ATNA demande si le bac a été vidé ;
 * 3. « Non » ouvre une requête aux Travaux publics, « Oui » clôt l'échange.
 */
export function useCollecteConversation(initialText: string, onOpenRequest?: () => void) {
  const seed = initialText.trim()
  const { arrived } = useLiveTruck()

  const [turns, setTurns] = useState<ShellTurn[]>(() => [{ id: 't0', role: 'user', text: seed }])
  const [thinking, setThinking] = useState(true)
  const [draft, setDraft] = useState('')
  const [photoAttached, setPhotoAttached] = useState(false)
  const [passedAt, setPassedAt] = useState<string | null>(() => (arrived ? timeNow() : null))

  const idRef = useRef(1)
  const timerRef = useRef<number | null>(null)
  const askedAfterPass = useRef(false)
  const requestOpened = useRef(false)

  const reply = useCallback(
    (kind: CollecteKind) => {
      if (kind === 'camion-passe') askedAfterPass.current = true
      if (kind === 'requete-ouverte' && !requestOpened.current) {
        requestOpened.current = true
        onOpenRequest?.()
      }
      if (timerRef.current) window.clearTimeout(timerRef.current)
      setThinking(true)
      timerRef.current = window.setTimeout(() => {
        idRef.current += 1
        setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'atna', kind }])
        setThinking(false)
      }, THINKING_MS)
    },
    [onOpenRequest],
  )

  const exchange = useCallback(
    (userText: string, kind: CollecteKind, withPhoto = false) => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'user', text: userText, withPhoto }])
      reply(kind)
    },
    [reply],
  )

  /** Réponse à un bac non ramassé selon que le camion est passé ou non. */
  const missedBinKind = useCallback((): CollecteKind => {
    if (!arrived) return 'camion-en-route'
    return askedAfterPass.current ? 'requete-ouverte' : 'camion-passe'
  }, [arrived])

  useEffect(() => {
    reply(isBatteryQuestion(seed) ? 'batterie' : isMissedBinReport(seed) ? missedBinKind() : 'reponse')
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lastAtna = useMemo(
    () => [...turns].reverse().find((turn): turn is ShellAtnaTurn => turn.role === 'atna'),
    [turns],
  )

  /** Le camion arrive pendant que la conversation est ouverte : ATNA relance d'elle-même. */
  useEffect(() => {
    if (!arrived) return
    setPassedAt((current) => current ?? timeNow())
    const followingTruck = turns.some((turn) => turn.role === 'atna' && turn.kind === 'camion-en-route')
    if (followingTruck && !askedAfterPass.current && !requestOpened.current) reply('camion-passe')
  }, [arrived, turns, reply])

  const confirmPickedUp = useCallback(() => exchange('Oui, mon bac a été vidé', 'bac-ok'), [exchange])
  const reportNotPickedUp = useCallback(
    () => exchange('Non, mon bac est toujours plein', 'requete-ouverte'),
    [exchange],
  )

  const findNearestDropOff = useCallback(
    () => exchange('Trouver le lieu le plus proche', 'batterie-lieux'),
    [exchange],
  )

  const attachPhoto = useCallback(() => {
    setPhotoAttached(true)
    exchange('Photo du bac', 'suivi', true)
  }, [exchange])

  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || thinking) return
    setDraft('')

    if (lastAtna?.kind === 'camion-passe') {
      if (isNo(text)) return exchange(text, 'requete-ouverte')
      if (isYes(text)) return exchange(text, 'bac-ok')
    }
    if (lastAtna?.kind === 'batterie' && /proche|où|ou |lieu|endroit|oui/i.test(text)) {
      return exchange(text, 'batterie-lieux')
    }
    exchange(text, isBatteryQuestion(text) ? 'batterie' : isMissedBinReport(text) ? missedBinKind() : 'suivi')
  }, [draft, exchange, lastAtna, missedBinKind, thinking])

  return {
    turns,
    thinking,
    draft,
    setDraft,
    submitDraft,
    attachPhoto,
    photoAttached,
    confirmPickedUp,
    reportNotPickedUp,
    findNearestDropOff,
    passedAt,
    liveAtnaId: lastAtna?.id,
    lastAtnaKind: lastAtna?.kind as CollecteKind | undefined,
  }
}
